import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BackButton } from "../../components/common/BackButton";
import { Button } from "../../components/common/Button";
import { PageTitle } from "../../components/common/PageData";
import { useCourseStore } from "../../stores/course.store";
import { useSeasonStore } from "../../stores/season.store";

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PosterImage = styled.img`
  width: 100%;
  max-height: 50vh;
  object-fit: contain;
  display: block;
  margin: 20px 0;
  cursor: pointer;
  border-radius: 8px;

  &:hover {
    opacity: 0.95;
  }
`;

const DescriptionContainer = styled.div`
  margin: 20px 0;
  padding: 20px;
  background-color: #f5f5f5;
  border-radius: 8px;
  line-height: 1.8;
  font-size: 18px;

  h1,
  h2,
  h3 {
    margin-top: 1em;
    margin-bottom: 0.5em;
  }

  h1 {
    font-size: 1.6em;
  }

  h2 {
    font-size: 1.4em;
  }

  h3 {
    font-size: 1.2em;
  }

  p {
    margin-bottom: 1em;
  }

  ul,
  ol {
    margin-bottom: 1em;
    padding-left: 24px;
  }

  li {
    margin-bottom: 0.5em;
  }

  a {
    color: #2196f3;
    text-decoration: underline;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 1em 0;
    font-size: 16px;
  }

  th,
  td {
    border: 1px solid #ddd;
    padding: 10px;
    text-align: left;
  }

  th {
    background-color: #e0e0e0;
    font-weight: bold;
  }

  tr:nth-child(even) {
    background-color: #fafafa;
  }
`;

const ButtonContainer = styled.div`
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e0e0e0;
`;

const OpeningSoonMessage = styled.div`
  background-color: #e3f2fd;
  border-radius: 8px;
  border: 1px solid #2196f3;
  text-align: center;
  font-size: 16px;
  color: #0d47a1;
  padding: 16px;
  margin-bottom: 24px;
  font-weight: 500;
`;

export const CourseDetails = () => {
  const navigate = useNavigate();
  const { currentSeason } = useSeasonStore();
  const { currentCourse, getCurrentCourse, isLoading } = useCourseStore();

  useEffect(() => {
    if (currentSeason) {
      getCurrentCourse();
    }
  }, [currentSeason, getCurrentCourse]);

  const handlePosterClick = () => {
    if (currentCourse?.poster_image) {
      window.open(currentCourse.poster_image, "_blank");
    }
  };

  // Check if subscriptions are open
  const getSubscriptionStatus = (): "before_opening" | "open" | "closed" => {
    if (!currentCourse) {
      return "closed";
    }
    const now = Date.now();
    const startDate = new Date(currentCourse.subscription_start_date).getTime();
    const endDate = new Date(currentCourse.subscription_end_date).getTime();

    if (now < startDate) {
      return "before_opening";
    }
    if (now > endDate) {
      return "closed";
    }
    return "open";
  };

  const subscriptionStatus = getSubscriptionStatus();

  const getOpeningDateMessage = () => {
    if (!currentCourse) {
      return "";
    }
    const openingDate = new Date(currentCourse.subscription_start_date);
    return `Le iscrizioni apriranno il ${format(openingDate, "d MMMM yyyy", {
      locale: it,
    })}`;
  };

  if (!currentSeason) {
    return (
      <PageContainer>
        <BackButton onClick={() => navigate("/account")} />
        <PageTitle>INFORMAZIONI CORSO</PageTitle>
        <p>Non ci sono corsi disponibili per la stagione corrente.</p>
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <BackButton onClick={() => navigate("/account")} />
        <PageTitle>INFORMAZIONI CORSO</PageTitle>
        <p>Caricamento...</p>
      </PageContainer>
    );
  }

  if (!currentCourse) {
    return (
      <PageContainer>
        <BackButton onClick={() => navigate("/account")} />
        <PageTitle>INFORMAZIONI CORSO</PageTitle>
        <p>Non ci sono corsi disponibili per la stagione corrente.</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackButton onClick={() => navigate("/account")} />
      <PageTitle>{currentCourse.title.toUpperCase()}</PageTitle>

      {subscriptionStatus === "before_opening" ? (
        <OpeningSoonMessage>{getOpeningDateMessage()}</OpeningSoonMessage>
      ) : (
        <ButtonContainer>
          <Button fullWidth onClick={() => navigate("/account/courses")}>
            GESTISCI ISCRIZIONE CORSI →
          </Button>
        </ButtonContainer>
      )}

      {currentCourse.poster_image && (
        <PosterImage
          src={currentCourse.poster_image}
          alt={currentCourse.title}
          onClick={handlePosterClick}
          title="Clicca per aprire in dimensioni reali"
          loading="eager"
          decoding="async"
          crossOrigin="anonymous"
        />
      )}

      {currentCourse.description && (
        <DescriptionContainer>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{currentCourse.description}</ReactMarkdown>
        </DescriptionContainer>
      )}
    </PageContainer>
  );
};
